import { DELETE_ALL_LEADS_CONFIRM, type AdminConnectorGrant } from "@teamna/shared";
import { useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  CopyMessageButton,
  ErrorState,
  Field,
  InlineError,
  Notice,
  PageHeader,
  SkeletonList,
  showToast,
} from "@teamna/ui/components/index.ts";
import { copyForError } from "@teamna/web-client/api/copy.ts";
import { useConnector, useDeleteAllLeads, useRevokeConnectorGrant } from "../lib/leads-api.ts";
import { lastActivityText } from "./format.ts";
import { LeadsTabs } from "./leads-parts.tsx";

/**
 * Leads settings, `/leads/settings`:
 *   - the connector: the address to add in Claude (phone, desktop, Claude Code) and the Claude apps
 *     signed in to it, each with Disconnect;
 *   - Delete all leads: Control Center has only production, so this is how a test starts again.
 *     Leads marked Do not contact stay. The labels stay unless the box is ticked.
 */

const onError = (error: Error) => showToast(copyForError(error).message, { tone: "info" });

export function LeadSettingsScreen() {
  return (
    <>
      <PageHeader title="Leads" description="Connect Claude to the Lead Finder, and start the list again." />
      <LeadsTabs current="settings" />
      <div className="flex flex-col gap-6">
        <ConnectorCard />
        <DeleteAllCard />
      </div>
    </>
  );
}

function ConnectorCard() {
  const connector = useConnector();
  const revoke = useRevokeConnectorGrant();
  const [revoking, setRevoking] = useState<AdminConnectorGrant | null>(null);

  return (
    <Card className="flex flex-col gap-4 p-4">
      <div>
        <h2 className="text-[16px] font-semibold text-ink">Connect Claude</h2>
        <p className="text-[13px] text-ink-2">
          Claude can push a lead from a chat on your phone, and do small runs from your laptop with Claude in Chrome. It never sends a message to anyone.
        </p>
      </div>
      {connector.isLoading ? (
        <SkeletonList rows={2} />
      ) : connector.error ? (
        <ErrorState error={connector.error} onRetry={() => void connector.refetch()} />
      ) : connector.data ? (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <code className="rounded-lg border border-line bg-sunken px-3 py-2 text-[13px] [overflow-wrap:anywhere] text-ink">{connector.data.url}</code>
            <CopyMessageButton text={connector.data.url} label="Copy address" />
          </div>
          <ol className="flex list-decimal flex-col gap-1.5 pl-5 text-[14px] text-ink-2">
            <li>In the Claude app, open Settings, Connectors, and add a custom connector with this address.</li>
            <li>Sign in with your @teamna.app Google account, then press Allow.</li>
            <li>
              On your laptop with Claude Code, run <code className="text-[13px] [overflow-wrap:anywhere]">claude mcp add --transport http teamna-cc {connector.data.url}</code>, then sign in from{" "}
              <code className="text-[13px]">/mcp</code>.
            </li>
          </ol>
          <section className="flex flex-col gap-2" aria-label="Signed-in Claude apps">
            <h3 className="text-[14px] font-semibold text-ink">Signed in now</h3>
            {connector.data.grants.length === 0 ? (
              <p className="text-[13px] text-ink-2">No Claude app is connected yet.</p>
            ) : (
              <ul className="flex flex-col divide-y divide-line rounded-xl border border-line">
                {connector.data.grants.map((grant) => (
                  <li key={grant.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5">
                    <div>
                      <p className="text-[14px] font-semibold text-ink">{grant.clientName}</p>
                      <p className="text-[12px] text-ink-2">
                        {grant.staffEmail} · connected {lastActivityText(grant.createdAt)} · last used {grant.lastUsedAt ? lastActivityText(grant.lastUsedAt) : "never"}
                      </p>
                    </div>
                    <Button variant="secondary" size="sm" onClick={() => setRevoking(grant)}>
                      Disconnect
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      ) : null}
      <ConfirmDialog
        open={revoking !== null}
        title={`Disconnect ${revoking?.clientName ?? "this app"}?`}
        confirmLabel="Disconnect"
        tone="danger"
        loading={revoke.isPending}
        onCancel={() => setRevoking(null)}
        onConfirm={() =>
          revoking &&
          revoke.mutate(revoking.id, {
            onSuccess: () => {
              showToast("Disconnected.");
              setRevoking(null);
            },
            onError,
          })
        }
      >
        <p>It stops at once. To use it again, connect it again from Claude.</p>
      </ConfirmDialog>
    </Card>
  );
}

function DeleteAllCard() {
  const deleteAll = useDeleteAllLeads();
  const [typed, setTyped] = useState("");
  const [alsoLabels, setAlsoLabels] = useState(false);
  const ready = typed === DELETE_ALL_LEADS_CONFIRM;

  return (
    <Card className="flex flex-col gap-4 p-4">
      <div>
        <h2 className="text-[16px] font-semibold text-ink">Start over</h2>
        <p className="text-[13px] text-ink-2">To delete the leads of one run only, open the run on the Runs tab.</p>
      </div>
      <Notice tone="danger" title="Delete all leads">
        This deletes every lead, every run and every label suggestion. Leads marked Do not contact stay, so a new run never brings them back. You cannot undo it.
      </Notice>
      {deleteAll.error ? <InlineError error={deleteAll.error} /> : null}
      <Field label={`Type ${DELETE_ALL_LEADS_CONFIRM} to confirm`} value={typed} onChange={(event) => setTyped(event.target.value)} autoComplete="off" />
      <Checkbox label="Also delete my labels (Already on Teamna stays)" checked={alsoLabels} onChange={setAlsoLabels} />
      <div>
        <Button
          variant="danger"
          disabled={!ready}
          loading={deleteAll.isPending}
          onClick={() =>
            deleteAll.mutate(
              { confirm: DELETE_ALL_LEADS_CONFIRM, alsoLabels },
              {
                onSuccess: (result) => {
                  setTyped("");
                  setAlsoLabels(false);
                  showToast(
                    `${result.deletedLeads} ${result.deletedLeads === 1 ? "lead" : "leads"} deleted. ${result.keptDoNotContact} Do not contact ${result.keptDoNotContact === 1 ? "lead stays" : "leads stay"}.`,
                  );
                },
              },
            )
          }
        >
          Delete all leads
        </Button>
      </div>
    </Card>
  );
}
